/**
 * Product editor — full-page form. Used both for `/reference/products/new`
 * (no `id` param) and `/reference/products/:id/edit`. Mirrors the Brand editor
 * conventions (local useState form, fromProduct / toCreateRequest /
 * toUpdateRequest helpers, MUI Grid + Paper sections, ErrorBanner, useSnackbar,
 * pending state).
 *
 * Backend rules respected (saas-auth spec 0010, shipped Java authoritative):
 *  - `slug` + `jwtAudience` are immutable — editable only at create, rendered
 *    read-only in edit mode and never sent in the update body.
 *  - `defaultBrandId` is NOT accepted at create (a brand must reference the
 *    product first); it is set later via PUT (edit mode only), and may be
 *    cleared to `null`. The picker offers only brands of THIS product.
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink, useLoaderData, useNavigate } from 'react-router-dom';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import { isDeployProblem } from '@/api/routingDeploy';
import { useCurrentOperator } from '@/auth/AuthContext';
import {
  APP_TOPOLOGIES,
  AUTH_MODES,
  MFA_POLICIES,
  type AppTopology,
  type AuthMode,
  type BrandDto,
  type CreateProductRequest,
  type MfaPolicy,
  type ProductDto,
  type ProductRehomeSummary,
  type UpdateProductRequest,
} from '@/api/types';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { ErrorBanner } from '@/components/ErrorBanner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';
import { deployProblemText } from '@/components/routingDeployText';

const SLUG_REGEX = /^[a-z0-9-]+$/;
// SASADM-0140 (saas-auth SASATH-0296) — the API context root is interpolated into a Caddy
// `rewrite` directive server-side, so a malformed value is a security concern, not a typo.
// Exactly one leading slash, then no further slash, no whitespace, and no `..` anywhere.
const API_CONTEXT_ROOT_REGEX = /^\/(?!.*\.\.)[^/\s]+$/;
// SASADM-0152 (saas-auth SASATH-0337) — the reseller MFA policy options, in the order the
// select offers them (REQUIRED is the server default).
const RESELLER_MFA_POLICY_OPTIONS: readonly { value: MfaPolicy; fallback: string }[] = [
  { value: 'REQUIRED', fallback: 'Required — "remember this device" allowed' },
  { value: 'ALWAYS', fallback: 'Always — every sign-in' },
  { value: 'OPTIONAL', fallback: 'Optional — each user chooses' },
  { value: 'DISABLED', fallback: 'Off' },
];

export interface ProductEditorLoaderData {
  product: ProductDto | null;
  brands: BrandDto[];
  // Spec 0059 — existing group names for the Group combobox. Falls back to `[]`
  // (the freeSolo field still lets the admin type a name) if the endpoint is
  // unavailable, mirroring the `brands` fetch's graceful degradation.
  groups: string[];
}

export async function productEditorLoader({
  params,
}: {
  params: { id?: string };
}): Promise<ProductEditorLoaderData> {
  const groups = await apiAdmin.products.groups().catch(() => [] as string[]);
  if (!params.id || params.id === 'new') {
    return { product: null, brands: [], groups };
  }
  const id = Number(params.id);
  const product = await apiAdmin.products.get(id);
  const brands = await apiAdmin.brands.list(id).catch(() => [] as BrandDto[]);
  return { product, brands, groups };
}

interface EditorState {
  slug: string;
  displayName: string;
  jwtAudience: string;
  defaultMfaPolicy: MfaPolicy;
  defaultMfaReverifyDays: number;
  defaultMfaTrustedDeviceDays: number;
  defaultBrandId: number | null;
  guestSignupUrl: string;
  guestSignupLabel: string;
  guestVerificationEnabled: boolean;
  productGroup: string;
  authMode: AuthMode;
  appTopology: AppTopology;
  apiContextRoot: string;
  productAdminMfaRequired: boolean;
  selfServiceTenantCreate: boolean;
  resellerMfaPolicy: MfaPolicy;
  baseDomain: string;
}

function fromProduct(p: ProductDto | null): EditorState {
  return {
    slug: p?.slug ?? '',
    displayName: p?.displayName ?? '',
    jwtAudience: p?.jwtAudience ?? '',
    defaultMfaPolicy: p?.defaultMfaPolicy ?? 'REQUIRED',
    defaultMfaReverifyDays: p?.defaultMfaReverifyDays ?? 30,
    defaultMfaTrustedDeviceDays: p?.defaultMfaTrustedDeviceDays ?? 30,
    defaultBrandId: p?.defaultBrandId ?? null,
    guestSignupUrl: p?.guestSignupUrl ?? '',
    guestSignupLabel: p?.guestSignupLabel ?? '',
    guestVerificationEnabled: p?.guestVerificationEnabled ?? false,
    // Create → empty (so the Group combobox opens on all options, and blank →
    // server stores `Default`); edit → the product's current group.
    productGroup: p?.productGroup ?? '',
    authMode: p?.authMode ?? 'PLATFORM',
    appTopology: p?.appTopology ?? 'COMPOSED',
    apiContextRoot: p?.apiContextRoot ?? '',
    productAdminMfaRequired: p?.productAdminMfaRequired ?? false,
    selfServiceTenantCreate: p?.selfServiceTenantCreate ?? false,
    resellerMfaPolicy: p?.resellerMfaPolicy ?? 'REQUIRED',
    baseDomain: p?.baseDomain ?? '',
  };
}

function toCreateRequest(state: EditorState, isSuperAdmin: boolean): CreateProductRequest {
  // `defaultBrandId` intentionally omitted — the backend rejects it at create.
  return {
    slug: state.slug.toLowerCase(),
    displayName: state.displayName,
    jwtAudience: state.jwtAudience,
    defaultMfaPolicy: state.defaultMfaPolicy,
    defaultMfaReverifyDays: state.defaultMfaReverifyDays,
    defaultMfaTrustedDeviceDays: state.defaultMfaTrustedDeviceDays,
    guestSignupUrl: state.guestSignupUrl || null,
    guestSignupLabel: state.guestSignupLabel || null,
    guestVerificationEnabled: state.guestVerificationEnabled,
    // Trim to keep casing/spacing consistent (blank → server lands in `Default`).
    productGroup: state.productGroup.trim(),
    authMode: state.authMode,
    appTopology: state.appTopology,
    apiContextRoot: state.apiContextRoot.trim() === '' ? null : state.apiContextRoot.trim(),
    productAdminMfaRequired: state.productAdminMfaRequired,
    // SASADM-0172 — SUPER_ADMIN only; anyone else never sends it.
    ...(isSuperAdmin ? { selfServiceTenantCreate: state.selfServiceTenantCreate } : {}),
  };
}

function toUpdateRequest(state: EditorState, isSuperAdmin: boolean): UpdateProductRequest {
  // `slug` + `jwtAudience` intentionally omitted — immutable on update.
  return {
    displayName: state.displayName,
    defaultMfaPolicy: state.defaultMfaPolicy,
    defaultMfaReverifyDays: state.defaultMfaReverifyDays,
    defaultMfaTrustedDeviceDays: state.defaultMfaTrustedDeviceDays,
    defaultBrandId: state.defaultBrandId,
    guestSignupUrl: state.guestSignupUrl || null,
    guestSignupLabel: state.guestSignupLabel || null,
    // Spec 0063 — ALWAYS send the current boolean. On PUT the server treats this
    // as a full replace (Boolean.TRUE.equals), so omitting it clobbers to false.
    guestVerificationEnabled: state.guestVerificationEnabled,
    // Submitted on the full object; "omit = unchanged" makes sending the current
    // value back safe. Trimmed for consistent casing/spacing.
    productGroup: state.productGroup.trim(),
    // Always send the current value — same rationale as guestVerificationEnabled/
    // productGroup (the editor submits the full object).
    authMode: state.authMode,
    // SASADM-0134 — always send the current value, identical rationale to authMode:
    // the editor's state is always the trusted-current value, so re-sending an
    // unchanged value is a safe no-op. No bespoke change-detection for this field.
    appTopology: state.appTopology,
    // SASADM-0140 — always send the current value, including while its SELF_CONTAINED-only
    // hidden field is unmounted. Empty means no route and is represented as null, never ''.
    apiContextRoot: state.apiContextRoot.trim() === '' ? null : state.apiContextRoot.trim(),
    // Spec 0119 — ALWAYS send the current boolean; same full-replace rationale as
    // guestVerificationEnabled.
    productAdminMfaRequired: state.productAdminMfaRequired,
    // SASADM-0152 — always send the current value (the server keeps it on omit, but the
    // editor submits the full object).
    resellerMfaPolicy: state.resellerMfaPolicy,
    // SASADM-0129 — full replace, like the other fields above. An empty input
    // submits `null`, never `''`. Changing this rehomes every existing tenant.
    baseDomain: state.baseDomain.trim() === '' ? null : state.baseDomain.trim(),
    // SASADM-0172 — SUPER_ADMIN only: always send the current boolean back; anyone else never
    // sends it.
    ...(isSuperAdmin ? { selfServiceTenantCreate: state.selfServiceTenantCreate } : {}),
  };
}

export function ProductEditor() {
  const { product, brands, groups } = useLoaderData() as ProductEditorLoaderData;
  const { t } = useCopy();
  const isNew = !product;
  const navigate = useNavigate();
  const snack = useSnackbar();
  const isSuperAdmin = useCurrentOperator()?.isSuperAdmin === true;
  const [state, setState] = useState<EditorState>(() => fromProduct(product));
  const isExternal = state.authMode === 'EXTERNAL';
  const [prevProduct, setPrevProduct] = useState(product);
  const [error, setError] = useState<ApiError | null>(null);
  const [pending, setPending] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // SASADM-0134 — packaging has no dry-run preview to hold, unlike confirmRehome.
  const [confirmPackaging, setConfirmPackaging] = useState(false);
  const guestUrlError = error?.code === 'INVALID_GUEST_SIGNUP_URL';
  const apiContextRootInvalid =
    state.apiContextRoot.trim() !== '' && !API_CONTEXT_ROOT_REGEX.test(state.apiContextRoot.trim());
  // SASADM-0129 — the dry-run rehome preview, shown in the confirm dialog (cleared
  // once the dialog closes, confirmed or cancelled).
  const [confirmRehome, setConfirmRehome] = useState<ProductRehomeSummary | null>(null);
  // SASADM-0129 — the committed rehome outcome (a collision-aborted dry run, or a
  // successful write), rendered in the persistent result panel below the form.
  const [rehomeResult, setRehomeResult] = useState<ProductRehomeSummary | null>(null);

  // Re-derive editor state when the loaded product changes via a render-phase
  // state adjustment (React's "adjust state while rendering" pattern) instead of
  // a synchronous setState in an effect — same result, no inter-render flash.
  if (product !== prevProduct) {
    setPrevProduct(product);
    setState(fromProduct(product));
  }

  function update<K extends keyof EditorState>(key: K, value: EditorState[K]) {
    setState((s) => ({ ...s, [key]: value }));
  }

  function updateDays(key: 'defaultMfaReverifyDays' | 'defaultMfaTrustedDeviceDays', raw: string) {
    const n = Number(raw);
    update(key, Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0);
  }

  async function onSave() {
    setError(null);
    setConfirmPackaging(false);
    if (isNew && state.slug.length > 0 && !SLUG_REGEX.test(state.slug)) {
      setError(new ApiError('UNKNOWN', 400, { details: { message: 'Invalid slug format' } }));
      return;
    }
    if (apiContextRootInvalid) {
      setError(new ApiError('UNKNOWN', 400, { details: { message: 'Invalid API context root' } }));
      return;
    }
    setPending(true);
    try {
      if (isNew) {
        const created = await apiAdmin.products.create(toCreateRequest(state, isSuperAdmin));
        snack.success(t('snackbar.productCreated', 'Product created.'));
        navigate(`/reference/products/${created.id}`);
      } else {
        // SASADM-0129 — the dry run runs only when baseDomain actually changed
        // (normalizing '' to null on both sides). An unchanged domain saves
        // exactly as it did before this unit — one call, no dialog, navigate.
        const nextDomain = state.baseDomain.trim() === '' ? null : state.baseDomain.trim();
        const domainChanged = nextDomain !== (product.baseDomain ?? null);
        // SASADM-0134 — packaging change confirmation gates entry to the rest of
        // this branch (including the base-domain flow below), regardless of
        // whether baseDomain also changed.
        const nextTopology = state.appTopology;
        const topologyChanged = nextTopology !== (product.appTopology ?? 'COMPOSED');

        if (topologyChanged && !confirmPackaging) {
          // Packaging changed and hasn't been confirmed yet — stop here and ask
          // first. Nothing is written. This return is inside the surrounding
          // try, so the existing `finally { setPending(false); }` already
          // resets pending.
          setConfirmPackaging(true);
          return;
        }

        if (!domainChanged) {
          await apiAdmin.products.update(product.id, toUpdateRequest(state, isSuperAdmin));
          snack.success(t('snackbar.productUpdated', 'Product updated.'));
          navigate(`/reference/products/${product.id}`);
        } else {
          setRehomeResult(null);
          const dryRun = await apiAdmin.products.update(
            product.id,
            toUpdateRequest(state, isSuperAdmin),
            {
              dryRun: true,
            },
          );
          const rehome = dryRun.rehome ?? null;
          if (rehome && (rehome.collisions ?? []).length > 0) {
            // B2 — collisions abort the whole operation; nothing was written.
            // No confirm dialog, no write, no path to "continue anyway".
            setRehomeResult(rehome);
          } else {
            setConfirmRehome(rehome);
          }
        }
      }
    } catch (err) {
      if (err instanceof ApiError) setError(err);
    } finally {
      setPending(false);
    }
  }

  // SASADM-0129 — confirmed: perform the real write (no dryRun). B1 — never
  // reached without a prior dry run. Stays on the page (design decision 1) and
  // renders the result panel instead of navigating away.
  async function onConfirmRehome() {
    if (!product) return;
    setError(null);
    setPending(true);
    try {
      const res = await apiAdmin.products.update(product.id, toUpdateRequest(state, isSuperAdmin));
      setRehomeResult(res.rehome ?? null);
      setConfirmRehome(null);
      snack.success(t('snackbar.productUpdated', 'Product updated.'));
    } catch (err) {
      if (err instanceof ApiError) setError(err);
      setConfirmRehome(null);
    } finally {
      setPending(false);
    }
  }

  function onCancelRehome() {
    // Cancel writes nothing — close the dialog and discard the dry-run preview.
    setConfirmRehome(null);
  }

  async function onDelete() {
    if (!product) return;
    setError(null);
    setPending(true);
    try {
      const outcome = await apiAdmin.products.delete(product.id);
      // SASADM-0176 — the page navigates away, so a problem deploy is a warning snackbar.
      if (isDeployProblem(outcome)) snack.warning(deployProblemText(outcome, t));
      else snack.success(t('snackbar.productDeleted', 'Product deleted.'));
      navigate('/reference/products');
    } catch (err) {
      if (err instanceof ApiError) {
        // 409 PRODUCT_IN_USE (and any other failure) — surface the backend
        // message via the banner and stay on the page.
        setError(err);
        setConfirmDelete(false);
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <Box>
      <Breadcrumbs
        items={[
          { label: t('nav.home', 'Home'), to: '/' },
          { label: t('nav.reference', 'Reference') },
          { label: t('nav.products', 'Products'), to: '/reference/products' },
          {
            label: isNew
              ? t('products.editor.newTitle', 'New product')
              : (product?.displayName ?? ''),
          },
        ]}
      />
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h1">
          {isNew ? t('products.editor.newTitle', 'New product') : product?.displayName}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            onClick={() =>
              navigate(isNew ? '/reference/products' : `/reference/products/${product.id}`)
            }
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => void onSave()}
            disabled={pending}
            startIcon={pending ? <CircularProgress size={16} /> : null}
          >
            {t('common.save', 'Save')}
          </Button>
        </Stack>
      </Stack>
      {/* INVALID_GUEST_SIGNUP_URL (0030) maps inline on the URL field, so keep it
          out of the banner. */}
      <ErrorBanner
        error={guestUrlError ? null : error}
        message={error?.code === 'PRODUCT_IN_USE' ? (error.details.message ?? null) : null}
      />
      {/* SASADM-0129 — the rehome result panel. Held in state so a successful
          rehome (or an aborted, collision-blocked one) stays visible instead of
          being discarded by a navigate (design decision 1). */}
      {rehomeResult ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          {(rehomeResult.collisions ?? []).length > 0 ? (
            // B2 — collisions abort the whole write; render nothing else, offer
            // no override control.
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {t(
                'products.baseDomain.collisions',
                'Nothing was changed. These target hostnames are already taken, so the rehome was aborted rather than leaving this product split across two domains:',
              )}
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 3 }}>
                {(rehomeResult.collisions ?? []).map((c, i) => (
                  <Typography
                    component="li"
                    key={`${c.tenantId}-${c.hostname}-${i}`}
                    variant="caption"
                  >
                    {c.slug || `#${c.tenantId}`} — {c.hostname}
                    {c.reason ? ` (${c.reason})` : ''}
                  </Typography>
                ))}
              </Box>
            </Alert>
          ) : (
            <>
              <Alert severity="success" sx={{ mt: 1.5 }}>
                {t(
                  'products.baseDomain.success',
                  'Rewrote {{rewritten}} hostnames across {{tenants}} tenants, moving them from {{from}} to {{to}}. {{leftAlone}} left alone.',
                  {
                    rewritten: rehomeResult.hostnamesRewritten,
                    tenants: rehomeResult.tenantsAffected,
                    leftAlone: rehomeResult.hostnamesLeftAlone,
                    from:
                      rehomeResult.fromBaseDomain ??
                      t('products.baseDomain.platformDefault', 'the platform default'),
                    to:
                      rehomeResult.toBaseDomain ??
                      t('products.baseDomain.platformDefault', 'the platform default'),
                  },
                )}
              </Alert>
              {rehomeResult.hostnamesLeftAlone > 0 ? (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  {t(
                    'products.baseDomain.confirmLeftAlone',
                    '{{count}} hostnames are deliberately left alone (vanity domains and platform-level rows). This is normal.',
                    { count: rehomeResult.hostnamesLeftAlone },
                  )}
                </Alert>
              ) : null}
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                {t(
                  'products.baseDomain.deployRequired',
                  'Not serving yet — the hostname rows were rewritten in the database, but routing has not been deployed. Until you deploy platform routing, the old hosts still serve and the new ones do not exist.',
                )}
                <Box sx={{ mt: 1 }}>
                  <Button component={RouterLink} to="/operations" variant="contained" size="small">
                    {t('routing.deploy.button', 'Deploy Platform Routing')}
                  </Button>
                </Box>
              </Alert>
            </>
          )}
        </Paper>
      ) : null}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h3" gutterBottom>
              {t('products.editor.identity', 'Identity')}
            </Typography>
            {/* Identity essentials — always shown, both auth modes, three
                two-column rows (SASADM-0131): Row 1 Display name + Group;
                Row 2 Slug + JWT audience (SASADM-0068 — hidden for EXTERNAL
                products via !isExternal, gate stays on the cell); Row 3 Base
                domain (SASADM-0129 — edit-only like defaultBrandId below,
                withheld at create via !isNew since CreateProductRequest
                doesn't carry it) + Authentication. */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('products.fields.displayName', 'Display name')}
                  value={state.displayName}
                  onChange={(e) => update('displayName', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                {/* Spec 0059 — Group: a freeSolo combobox (pick an existing name or
                    type a new one). Options from products.groups(); suggesting them
                    keeps casing consistent (avoids `Consumer` vs `consumer`). The
                    input text is the source of truth and is trimmed on submit. The
                    placeholder hints that blank on create → server stores `Default`. */}
                <Autocomplete
                  freeSolo
                  options={groups}
                  inputValue={state.productGroup}
                  onInputChange={(_, value) => update('productGroup', value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label={t('products.fields.productGroup', 'Group')}
                      placeholder={t('products.fields.productGroupPlaceholder', 'Default')}
                      helperText={t(
                        'products.fields.productGroupHelper',
                        'Pick an existing group or type a new one.',
                      )}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('products.fields.slug', 'Slug')}
                  value={state.slug}
                  onChange={(e) => update('slug', e.target.value.toLowerCase())}
                  disabled={!isNew}
                  helperText={
                    !isNew
                      ? t('products.fields.slugImmutable', "Can't be changed after create")
                      : ''
                  }
                />
              </Grid>
              {!isExternal ? (
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    required
                    label={t('products.fields.jwtAudience', 'JWT audience')}
                    value={state.jwtAudience}
                    onChange={(e) => update('jwtAudience', e.target.value)}
                    disabled={!isNew}
                    helperText={
                      !isNew
                        ? t('products.fields.jwtAudienceImmutable', "Can't be changed after create")
                        : ''
                    }
                  />
                </Grid>
              ) : null}
              {!isNew ? (
                <Grid size={{ xs: 12, md: 6 }}>
                  {/* SASADM-0129 — this product's own base domain. Changing it rehomes
                      every existing tenant of this product (dry run → confirm → write;
                      see the confirm dialog + result panel below). Edit-only — set
                      later via PUT, same as defaultBrandId — since
                      CreateProductRequest doesn't accept it. */}
                  <TextField
                    fullWidth
                    label={t('products.fields.baseDomain', 'Base domain')}
                    value={state.baseDomain}
                    onChange={(e) => update('baseDomain', e.target.value)}
                    helperText={t(
                      'products.baseDomain.helper',
                      'Leave empty to use the platform default. Changing this rehomes every existing tenant of this product.',
                    )}
                  />
                </Grid>
              ) : null}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  select
                  fullWidth
                  required
                  label={t('products.fields.authMode', 'Authentication')}
                  value={state.authMode}
                  onChange={(e) => update('authMode', e.target.value as AuthMode)}
                  helperText={t(
                    'products.fields.authModeHelper',
                    'External marks a product whose apps sign in via their own SSO (e.g. Entra). Presentation only — it changes no authentication.',
                  )}
                >
                  {AUTH_MODES.map((m) => (
                    <MenuItem key={m} value={m}>
                      {t(`products.authModes.${m}`, m)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                {/* SASADM-0134 — packaging, independent of authMode. Ungated (no
                    isNew/isExternal wrapper) — available at create and edit,
                    regardless of authMode. */}
                <TextField
                  select
                  fullWidth
                  required
                  label={t('products.fields.appTopology', 'Packaging')}
                  value={state.appTopology}
                  onChange={(e) => update('appTopology', e.target.value as AppTopology)}
                  helperText={t(
                    'products.fields.appTopologyHelper',
                    'Composed — the UI and API are separate hosts. Self-contained — one WAR serves both behind a single public URL.',
                  )}
                >
                  {APP_TOPOLOGIES.map((v) => (
                    <MenuItem key={v} value={v}>
                      {t(`products.packaging.${v}`, v)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {state.appTopology !== 'SELF_CONTAINED' ? (
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label={t('products.fields.apiContextRoot', 'API context root')}
                    placeholder="/dink-api"
                    value={state.apiContextRoot}
                    onChange={(e) => update('apiContextRoot', e.target.value)}
                    error={apiContextRootInvalid}
                    helperText={
                      apiContextRootInvalid
                        ? t(
                            'products.fields.apiContextRootInvalid',
                            'Must start with “/” and contain no spaces, no “..”, and no second “/”.',
                          )
                        : t(
                            'products.fields.apiContextRootHelper',
                            'The path the product’s API is deployed under on the app server. Never shown to users — requests arrive at /api and are rewritten to this.',
                          )
                    }
                  />
                </Grid>
              ) : null}
            </Grid>

            {/* SASADM-0172 (SASATH-0388 §2) — a product posture switch, SUPER_ADMIN only; independent
                of the auth mode, so it sits outside the platform-only region. */}
            {isSuperAdmin ? (
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={state.selfServiceTenantCreate}
                      onChange={(e) => update('selfServiceTenantCreate', e.target.checked)}
                    />
                  }
                  label={t(
                    'products.fields.selfServiceTenantCreate',
                    'Allow self-service tenant creation',
                  )}
                />
                <Typography
                  variant="caption"
                  sx={{ display: 'block', color: 'text.secondary', ml: 6 }}
                >
                  {t(
                    'products.fields.selfServiceTenantCreateHelper',
                    "Lets this product's backend create tenants from its own public signup, server to server, with no admin. Capped per day.",
                  )}
                </Typography>
              </Box>
            ) : null}

            {/* Platform-only region — hidden for EXTERNAL-auth products (their apps
                sign in via their own SSO, so JWT audience / MFA defaults / guest
                signup / default brand don't apply; the backend defaults the JWT
                audience for external products). Hiding only UNMOUNTS these inputs —
                the EditorState (and therefore the submitted payload) is untouched,
                so a product switched to External keeps its stored values. */}
            {!isExternal && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h3" gutterBottom>
                  {t('products.editor.mfaDefaults', 'MFA defaults')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      select
                      fullWidth
                      required
                      label={t('products.fields.defaultMfaPolicy', 'Default MFA policy')}
                      value={state.defaultMfaPolicy}
                      onChange={(e) => update('defaultMfaPolicy', e.target.value as MfaPolicy)}
                    >
                      {MFA_POLICIES.map((p) => (
                        <MenuItem key={p} value={p}>
                          {t(`products.mfa.${p}`, p)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      required
                      type="number"
                      label={t('products.fields.defaultMfaReverifyDays', 'Reverify days')}
                      value={String(state.defaultMfaReverifyDays)}
                      onChange={(e) => updateDays('defaultMfaReverifyDays', e.target.value)}
                      slotProps={{ htmlInput: { min: 0 } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      required
                      type="number"
                      label={t(
                        'products.fields.defaultMfaTrustedDeviceDays',
                        'Trusted-device days',
                      )}
                      value={String(state.defaultMfaTrustedDeviceDays)}
                      onChange={(e) => updateDays('defaultMfaTrustedDeviceDays', e.target.value)}
                      slotProps={{ htmlInput: { min: 0 } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: isNew ? 12 : 8 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={state.productAdminMfaRequired}
                          onChange={(e) => update('productAdminMfaRequired', e.target.checked)}
                        />
                      }
                      label={t(
                        'products.fields.productAdminMfaRequired',
                        'Require MFA for product administrators',
                      )}
                    />
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: 'text.secondary', ml: 4 }}
                    >
                      {t(
                        'products.fields.productAdminMfaRequiredHelper',
                        "When on, anyone signing in to this product's admin console must complete an email verification code every time. Applies to the product-admin console only — not tenant/customer logins.",
                      )}
                    </Typography>
                  </Grid>
                  {/* SASADM-0152 — edit only: saas-auth's create request has no reseller
                      MFA policy (a new product stores REQUIRED); it saves through the
                      product update. */}
                  {!isNew ? (
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        select
                        fullWidth
                        label={t('products.fields.resellerMfaPolicy', 'Reseller MFA policy')}
                        value={state.resellerMfaPolicy}
                        onChange={(e) => update('resellerMfaPolicy', e.target.value as MfaPolicy)}
                        helperText={t(
                          'products.fields.resellerMfaPolicyHelper',
                          "Applies to reseller users signing in to this product's reseller console.",
                        )}
                      >
                        {RESELLER_MFA_POLICY_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {t(
                              `products.fields.resellerMfaPolicyOptions.${option.value}`,
                              option.fallback,
                            )}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  ) : null}
                </Grid>

                <Divider sx={{ my: 3 }} />
                <Typography variant="h3" gutterBottom>
                  {t('products.editor.guestSignup', 'Guest signup')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={state.guestVerificationEnabled}
                          onChange={(e) => update('guestVerificationEnabled', e.target.checked)}
                        />
                      }
                      label={t(
                        'products.fields.guestVerificationEnabled',
                        'Enable guest email verification',
                      )}
                    />
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: 'text.secondary', ml: 4 }}
                    >
                      {t(
                        'products.fields.guestVerificationEnabledHelper',
                        'Lets tenants of this product issue guest email-verification codes for guest checkout. Applies to every tenant of this product.',
                      )}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label={t('products.fields.guestSignupUrl', 'Guest signup URL')}
                      value={state.guestSignupUrl}
                      onChange={(e) => update('guestSignupUrl', e.target.value)}
                      placeholder="/guest/book"
                      error={guestUrlError}
                      helperText={
                        guestUrlError
                          ? t('errors.codes.INVALID_GUEST_SIGNUP_URL', 'Invalid guest signup URL')
                          : t(
                              'products.fields.guestSignupUrlHelper',
                              'Relative path starting with “/”. Applies to all tenants of this product.',
                            )
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label={t('products.fields.guestSignupLabel', 'Guest signup label')}
                      value={state.guestSignupLabel}
                      onChange={(e) => update('guestSignupLabel', e.target.value)}
                      placeholder={t('brands.guestSignup.defaultLabel', 'Continue as guest')}
                      helperText={t(
                        'products.fields.guestSignupLabelHelper',
                        'Link text on the login page. Defaults to “Continue as guest” when blank.',
                      )}
                    />
                  </Grid>
                </Grid>

                {!isNew ? (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h3" gutterBottom>
                      {t('products.editor.defaultBrand', 'Default brand')}
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      label={t('products.fields.defaultBrandId', 'Default brand')}
                      value={state.defaultBrandId === null ? '' : String(state.defaultBrandId)}
                      onChange={(e) =>
                        update(
                          'defaultBrandId',
                          e.target.value === '' ? null : Number(e.target.value),
                        )
                      }
                      helperText={t(
                        'products.fields.defaultBrandHelper',
                        'Only brands of this product. Clear to inherit none.',
                      )}
                    >
                      <MenuItem value="">
                        <em>{t('products.fields.defaultBrandNone', '— None —')}</em>
                      </MenuItem>
                      {brands.map((b) => (
                        <MenuItem key={b.id} value={b.id}>
                          {b.displayName} ({b.slug})
                        </MenuItem>
                      ))}
                    </TextField>
                  </>
                ) : null}
              </>
            )}
          </Paper>

          {!isNew ? (
            <Paper variant="outlined" sx={{ p: 3, mt: 2 }}>
              <Typography variant="h3" gutterBottom>
                {t('products.editor.delete', 'Delete product')}
              </Typography>
              <Button color="error" variant="outlined" onClick={() => setConfirmDelete(true)}>
                {t('common.delete', 'Delete')}
              </Button>
            </Paper>
          ) : null}
        </Grid>
      </Grid>
      <ConfirmDialog
        open={confirmDelete}
        title={t('products.editor.delete', 'Delete product')}
        body={t('products.editor.deleteBody', 'Delete {{name}}? This cannot be undone.', {
          name: product?.displayName ?? '',
        })}
        severity="error"
        {...(product?.displayName ? { requireTypedConfirmation: product.displayName } : {})}
        busy={pending}
        onConfirm={() => void onDelete()}
        onCancel={() => setConfirmDelete(false)}
      />
      {/* SASADM-0134 — packaging change confirmation. onConfirm re-invokes onSave()
          with confirmPackaging already true (the dialog only renders when it is),
          so the guard in onSave is skipped on this second pass and the flow
          proceeds to the base-domain branch — mirrors the rehome dialog's
          "confirm re-enters the same async flow" idiom. */}
      <ConfirmDialog
        open={confirmPackaging}
        title={t('products.packaging.confirmTitle', 'Change packaging?')}
        body={t(
          'products.packaging.confirmBody',
          'Changing packaging does NOT clean up existing hostnames. It affects newly provisioned tenants only. Existing tenants keep the hostnames they already have — remove them manually from each tenant’s Whitelabel tab if they are no longer wanted.',
        )}
        severity="warning"
        confirmLabel={t('products.packaging.confirmAction', 'Change packaging')}
        busy={pending}
        onConfirm={() => {
          setConfirmPackaging(true);
          void onSave();
        }}
        onCancel={() => setConfirmPackaging(false)}
      />
      {/* SASADM-0129 — B1: always dry-run first. This dialog only ever opens with
          the real numbers from that dry run; there is no straight-through save
          when the base domain changed. */}
      <ConfirmDialog
        open={confirmRehome !== null}
        title={t('products.baseDomain.confirmTitle', 'Rehome tenants to a new base domain?')}
        body={
          <>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {t(
                'products.baseDomain.confirmBody',
                'This will rewrite {{rewritten}} hostnames across {{tenants}} tenants and move them from {{from}} to {{to}}.',
                {
                  rewritten: confirmRehome?.hostnamesRewritten ?? 0,
                  tenants: confirmRehome?.tenantsAffected ?? 0,
                  from:
                    confirmRehome?.fromBaseDomain ??
                    t('products.baseDomain.platformDefault', 'the platform default'),
                  to:
                    confirmRehome?.toBaseDomain ??
                    t('products.baseDomain.platformDefault', 'the platform default'),
                },
              )}
            </Typography>
            {(confirmRehome?.hostnamesLeftAlone ?? 0) > 0 ? (
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                {t(
                  'products.baseDomain.confirmLeftAlone',
                  '{{count}} hostnames are deliberately left alone (vanity domains and platform-level rows). This is normal.',
                  { count: confirmRehome?.hostnamesLeftAlone ?? 0 },
                )}
              </Typography>
            ) : null}
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {t(
                'products.baseDomain.confirmOrder',
                'Do these in order: 1. point DNS *.{{to}} at the edge, 2. run this rehome, 3. deploy platform routing, 4. re-register OAuth redirect URIs (Google, Entra, GHL).',
                {
                  to:
                    confirmRehome?.toBaseDomain ??
                    t('products.baseDomain.platformDefault', 'the platform default'),
                },
              )}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t(
                'products.baseDomain.confirmSessions',
                'Users of rehomed tenants are signed out once: the session cookie is scoped to the tenant apex, whose domain is changing.',
              )}
            </Typography>
            <Typography variant="body2">
              {t(
                'products.baseDomain.confirmOauth',
                'External OAuth redirect URIs will break. Google, Entra and GHL hold callbacks on the old hostnames and nothing here can update them — the symptom is a login that fails only for social/SSO users.',
              )}
            </Typography>
          </>
        }
        severity="warning"
        confirmLabel={t('products.baseDomain.confirmAction', 'Rehome tenants')}
        busy={pending}
        onConfirm={() => void onConfirmRehome()}
        onCancel={onCancelRehome}
      />
    </Box>
  );
}
